import {
  Box,
  styled,
  MenuItem,
  Select as MuiSelect,
  FormControl,
  InputBase,
} from "@mui/material";
import { format } from "date-fns";
import { useMemo, useState } from "react";

import { useGetBlacklistQuery } from "api/blacklist";
import { DataGrid } from "components/DataGrid";
import { SearchIcon } from "components/Icons";

import { UpdateFlaggedAccountStatus } from "./components/UpdateFlaggedAccountStatus";
import { FlaggedAccountMenu } from "./components/FlaggedAccountMenu";

export const ACCOUNT_FLAG_STATUS_TYPES = {
  FLAGGED: 1,
  UNFLAGGED: 0,
};

const Search = styled("div")(({ theme }) => ({
  position: "relative",
}));

const Select = styled(MuiSelect)(() => ({
  ".MuiOutlinedInput-notchedOutline": {
    border: "1px solid #EFF1F4 !important",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: "1.5px solid #EFF1F4",
  borderRadius: "8px",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const StatusStyle = styled(Box)(() => ({
  display: "inline-flex",
  justifyContent: "center",
  borderRadius: "20px",
  padding: "5px 15px",
}));

const columns = ({ handleUpdateAccount }) => [
  { field: "name", headerName: "Name", flex: 1 },
  { field: "code", headerName: "Code/Account", flex: 1 },
  {
    field: "status",
    headerName: "STATUS",
    flex: 1,
    renderCell: ({ row }) => {
      if (parseInt(row.status) === ACCOUNT_FLAG_STATUS_TYPES.FLAGGED) {
        return (
          <StatusStyle
            sx={{
              backgroundColor: "#E9F8F3",
              color: "#25B883",
            }}
          >
            Flagged
          </StatusStyle>
        );
      } else if (parseInt(row.status) === ACCOUNT_FLAG_STATUS_TYPES.UNFLAGGED) {
        return (
          <StatusStyle
            sx={{
              backgroundColor: "#FEEFF0",
              color: "#F25A68",
            }}
          >
            Unflagged
          </StatusStyle>
        );
      } else {
        return "-";
      }
    },
  },
  {
    field: "createdOn",
    headerName: "DATE & TIME",
    valueFormatter: ({ value }) =>
      format(new Date(Number(value) * 1000), "dd/MM/yyyy hh:mm a"),
    flex: 1,
  },
  {
    field: "",
    headerName: "",
    align: "center",
    renderCell: (params) => (
      <FlaggedAccountMenu
        handleUpdateAccount={handleUpdateAccount}
        account={params.row}
      />
    ),
  },
];

export const FlaggedAccounts = () => {
  const { data = [], isLoading } = useGetBlacklistQuery();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      String(item.name)
        .trim()
        .toLowerCase()
        .includes(search.trim().toLowerCase()) && status === ""
        ? true
        : parseInt(item.status) === parseInt(status)
    );
  }, [status, search, data]);

  const handleUpdateAccount = (account) => {
    setSelectedAccount(account);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  return (
    <Box paddingTop={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search"
            inputProps={{ "aria-label": "search" }}
            value={search}
            onChange={handleSearch}
          />
        </Search>
        <Box display="flex" alignItems="center" gap={0.5}>
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <Select onChange={handleStatusChange} value={status} displayEmpty>
              <MenuItem value="">All</MenuItem>
              {Object.entries(ACCOUNT_FLAG_STATUS_TYPES).map(
                ([label, value]) => (
                  <MenuItem key={value} value={value}>
                    {label.charAt(0).toUpperCase() +
                      label.substring(1).toLowerCase()}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <DataGrid
        loading={isLoading}
        rows={filteredData}
        columns={columns({ handleUpdateAccount })}
        disableColumnMenu
        disableRowSelectionOnClick
      />
      <UpdateFlaggedAccountStatus
        open={Boolean(selectedAccount)}
        account={selectedAccount}
        handleClose={() => setSelectedAccount(null)}
      />
    </Box>
  );
};
